import { Injectable } from '@angular/core';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { StatisticsService } from './statistics.service';
import { StatisticsExportWidget, StatisticsTabKey } from './statistics-export.models';

@Injectable({ providedIn: 'root' })
export class StatisticsPngExportService {
  /** Set by statistics.component whenever the active tab changes. */
  currentTabKey: StatisticsTabKey = 'alerts';

  /** True while any widget PNG capture is in progress (disables all buttons). */
  captureInProgress = false;

  constructor(private statistics: StatisticsService) {}

  /**
   * True when the current tab is ready to export. Delegates to
   * StatisticsService.isTabExportReady(), which is stricter than "data loaded":
   * it also requires paging to be complete and the tab's data to be cached.
   */
  isTabDataReady(): boolean {
    return this.statistics.isTabExportReady(this.currentTabKey);
  }

  /**
   * Capture the widget and download the result as a PNG file.
   * Filename: {slug}-{tab}-{startDate}_{endDate}.png
   */
  async downloadWidgetPng(widget: StatisticsExportWidget): Promise<void> {
    if (this.captureInProgress) {
      return;
    }
    this.captureInProgress = true;
    try {
      const snapshot = await widget.captureForExport('png');
      if (!snapshot?.imageDataUrl) {
        // The widget had no data to render for the selected range. The download
        // button is gated on tab readiness, not per-widget data, so surface a
        // diagnostic instead of silently producing no file.
        console.warn(
          `PNG export skipped: widget "${snapshot?.slug ?? 'unknown'}" on tab "${this.currentTabKey}" produced no image.`
        );
        return;
      }
      const filename = this.buildFilename(snapshot.slug);
      this.saveFile(snapshot.imageDataUrl, filename);
    } finally {
      this.captureInProgress = false;
    }
  }

  // Thin wrapper around file-saver so tests can spy on it (the ESM
  // namespace import is not writable under the esbuild test bundler).
  protected saveFile(data: string | Blob, filename: string): void {
    saveAs(data, filename);
  }

  private buildFilename(slug: string): string {
    const start = moment(this.statistics.filters.startDate).utc().format('YYYY-MM-DD');
    const end = moment(this.statistics.filters.endDate).utc().format('YYYY-MM-DD');
    const raw = `${slug}-${this.currentTabKey}-${start}_${end}.png`;
    return raw.replace(/[/\\?%*:|"<>]/g, '-');
  }
}
